<template>
  <div>
    <v-card-title>
      Tag Details
    </v-card-title>
    <v-card-text class="pb-0">

      <v-text-field v-model="form.name"
                    label="Tag Name"
                    hint="Must be unique for the template"
                    persistent-hint
                    outlined
                    dense
                    :error-messages="form.errors.get('name')"
                    class="mb-4">
      </v-text-field>

      <v-checkbox v-model="form.is_required"
                  label="Require users to fill in this tag before sending an email"
                  dense
                  hide-details
                  class="mt-0 mb-5 py-0">
      </v-checkbox>

      <v-text-field v-model="form.instructions"
                    label="Instructions (Optional)"
                    placeholder="Optional"
                    outlined
                    dense
                    :error-messages="form.errors.get('instructions')">
      </v-text-field>

      <v-select v-model="form.tag_type"
                label="Tag Type"
                :items="customTagTypes"
                item-value="id"
                item-text="text"
                item-disabled="disabled"
                outlined
                dense>
      </v-select>

      <v-select v-if="isDate"
                v-model="form.settings.date_format"
                :items="dateFormats"
                item-value="value"
                item-text="text"
                label="Date format"
                outlined
                dense>
      </v-select>

      <template v-if="isTime">
        <v-select v-model="form.settings.time_format"
                  :items="timeFormats"
                  item-value="value"
                  item-text="text"
                  label="Time format"
                  outlined
                  dense>
        </v-select>
      </template>

      <v-textarea v-if="isCRMQuery"
                  v-model="form.settings.crm_query"
                  label="CRM Query"
                  rows="4"
                  outlined
                  dense>
      </v-textarea>


    </v-card-text>

    <template v-if="hasOptions">
      <v-card-title class="py-0">
        List Options
      </v-card-title>
      <v-card-text class="pb-0">
        <v-list dense>
          <draggable v-model="form.options"
                     handle=".handle">
            <v-list-item v-for="(option, index) in form.options"
                         :key="index"
                         class="pl-0 border-gray-light">
              <v-list-item-icon class="mt-5 mb-0 mx-2 handle"
                                style="cursor: grab;">
                <v-icon>
                  fas fa-fw fa-grip-lines
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-text-field v-if="isList"
                              v-model="form.options[index].value"
                              :label="`Option ${index+1}`"
                              :disabled="form.busy || !form.name"
                              outlined
                              dense
                              hide-details>
                </v-text-field>
                <div v-else-if="isHTML">
                  <v-text-field v-model.trim="form.options[index].name"
                                :label="`Option ${index+1} Name`"
                                placeholder="Option Name"
                                :disabled="form.busy || !form.name"
                                outlined
                                dense>
                  </v-text-field>
                  <v-textarea v-model="form.options[index].value"
                              :label="`Option ${index+1} HTML code`"
                              :disabled="form.busy || !form.name"
                              outlined
                              dense
                              rows="5"
                              hide-details>
                  </v-textarea>
                </div>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn @click.prevent="removeOption(index)"
                       icon>
                  <v-icon small>
                    fas fa-fw fa-trash
                  </v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-list-item class="pl-0">
              <v-list-item-content>
                <div v-if="isList">
                  <v-text-field v-model.trim="newOptionValue"
                                ref="newOptionValue"
                                :disabled="form.busy || !form.name"
                                label="New Option"
                                outlined
                                dense
                                @keydown="handleKeydown"
                                @keydown.enter.prevent="addOption"
                                @blur="addOption"
                                hide-details>
                  </v-text-field>
                </div>
                <div v-else-if="isHTML">
                  <v-text-field v-model.trim="newOptionName"
                                ref="newOptionName"
                                :disabled="form.busy || !form.name"
                                label="New HTML Option Name"
                                placeholder="Option Name"
                                outlined
                                dense
                                hint="The user will see this in the picklist.">
                  </v-text-field>
                  <v-textarea v-model.trim="newOptionValue"
                              ref="newOptionValue"
                              :disabled="form.busy || !form.name"
                              label="New HTML Option Code"
                              placeholder="Paste the HTML code here."
                              outlined
                              dense
                              rows="5"
                              @keydown="handleKeydown"
                              @keydown.enter.prevent="addOption"
                              @blur="addOption"
                              hide-details>
                  </v-textarea>
                </div>

              </v-list-item-content>
            </v-list-item>
          </draggable>
        </v-list>
      </v-card-text>
    </template>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';
import shareTemplateService from '../../../../../ml/services/shareTemplateService';
import draggable from 'vuedraggable';
import {v4 as uuidv4} from 'uuid';

export default {
  components: {draggable},
  props: {
    form: {
      type: Object,
      required: true,
    },
    customTag: {
      type: Object,
      required: false,
    },
    template: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      newOptionName: null,
      newOptionValue: null,
      customTagTypes: shareTemplateService.getCustomTagTypes(),
      dateFormats: shareTemplateService.getDateFormats(), // MLD-7076
      timeFormats: shareTemplateService.getTimeFormats(), // MLD-7076
      timeSteps: [
        {
          value: '1',
          text: '1 minute',
        },
        {
          value: '15',
          text: '15 minutes',
        },
        {
          value: '30',
          text: '30 minutes',
        },
      ]
    }
  },
  computed: {
    ...mapGetters([
      'shareTemplates',
    ]),
    hasOptions() {
      return this.isList || this.isHTML;
    },
    isList() {
      return this.form.tag_type === 'List';
    },
    isHTML() {
      return this.form.tag_type === 'HTML';
    },
    isText() {
      return this.form.tag_type === 'Text';
    },
    isDate() { // MLD-7076
      return this.form.tag_type === 'Date';
    },
    isTime() { // MLD-7076
      return this.form.tag_type === 'Time';
    },
    isCRMQuery() { // MLD-7075
      return this.form.tag_type === 'CRM Query';
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      //this.$store.dispatch('fetchShareTemplates');
    },
    handleKeydown(event) {
      if (event.key === 'Tab') {
        event.preventDefault();
        this.addOption();
        this.$refs.newOptionValue.focus();
      }
    },
    optionValueExists(optionName) {
      return this.form.options.some(option => option.name === optionName).length > 0;
    },
    updateNewOptionName() {
      this.newOptionName = `Option ${this.form.options.length + 1}`;
    },
    addOption() {
      if (!this.newOptionValue) {
        return;
      }
      if (!this.newOptionName) {
        this.updateNewOptionName();
      }
      if (this.newOptionValue && this.optionValueExists(this.newOptionValue) === false) {
        const optionObject = {
          id: uuidv4(),
          name: this.newOptionName,
          value: this.newOptionValue,
        }
        this.form.options.push(optionObject);
        this.newOptionValue = null;
        this.updateNewOptionName();
      } else {
        this.$toast.error('Invalid option value.');
      }
    },
    removeOption(index) {
      if (index >= 0 && index < this.form.options.length) {
        this.form.options.splice(index, 1);
      }
    },
    closeDialog() {
      this.$emit('close-dialog');
    }
  }
}
</script>
